import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { FiscalYear, FiscalYearCreate } from '../types'

const QUERY_KEY = 'fiscal_years'

export function useFiscalYears(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, residenceId],
    queryFn: async () => {
      let query = supabase
        .from('fiscal_years')
        .select('*')
        .order('start_date', { ascending: false })

      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as FiscalYear[]
    },
    enabled: !!residenceId,
  })
}

export function useFiscalYear(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data as FiscalYear
    },
    enabled: !!id,
  })
}

export function useCurrentFiscalYear(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'current', residenceId],
    queryFn: async () => {
      // 1. Chercher l'exercice avec statut 'open' (exercice en cours)
      const { data: openFY, error: openError } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('residence_id', residenceId!)
        .eq('status', 'open')
        .order('start_date', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (openError) throw openError
      if (openFY) return openFY as FiscalYear

      // 2. Sinon, le dernier exercice créé (draft, le plus récent)
      const { data: draftFY, error: draftError } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('residence_id', residenceId!)
        .eq('status', 'draft')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (draftError) throw draftError
      if (draftFY) return draftFY as FiscalYear

      // 3. Sinon, le dernier exercice clôturé
      const { data: closedFY, error: closedError } = await supabase
        .from('fiscal_years')
        .select('*')
        .eq('residence_id', residenceId!)
        .eq('status', 'closed')
        .order('end_date', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (closedError) throw closedError
      return closedFY as FiscalYear | null
    },
    enabled: !!residenceId,
  })
}

export function useCreateFiscalYear() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: FiscalYearCreate) => {
      const { data: result, error } = await supabase
        .from('fiscal_years')
        .insert(data)
        .select()
        .single()
      if (error) throw error
      return result as FiscalYear
    },
    onSuccess: () => {
      // Invalidate all fiscal year queries (list, current, detail)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateFiscalYear() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<FiscalYear> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('fiscal_years')
        .update(data)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return result as FiscalYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useStartFiscalYear() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: result, error } = await supabase
        .from('fiscal_years')
        .update({ status: 'open' })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return result as FiscalYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useCloseFiscalYear() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: result, error } = await supabase
        .from('fiscal_years')
        .update({ status: 'closed', closed_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return result as FiscalYear
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}
